import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { setActiveClient } from "@/utils/clientWorkspace";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Building2,
  FolderKanban,
  Mail,
  Phone,
  Plus,
  ShieldCheck,
  UserCircle2,
  Users,
  ArrowRight,
  Edit,
  UserCog,
  BriefcaseBusiness,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emptyEmployeeForm = {
  name: "",
  email: "",
  phone_number: "",
  role: "",
  access_level: "member",
  password: "",
};

const emptyClientForm = {
  name: "",
  email: "",
  password: "",
  company_gst_number: "",
  company_type: "product_based",
  assigned_team_member_ids: [],
};

export default function TeamMembers() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isOrgOwner = user.account_type === "organization";
  const isTeamMember = user.account_type === "team_member";

  const [loading, setLoading] = useState(true);
  const [teamMembers, setTeamMembers] = useState([]);
  const [clients, setClients] = useState([]);
  const [employeeDialogOpen, setEmployeeDialogOpen] = useState(false);
  const [clientDialogOpen, setClientDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [editingClient, setEditingClient] = useState(null);
  const [employeeForm, setEmployeeForm] = useState(emptyEmployeeForm);
  const [clientForm, setClientForm] = useState(emptyClientForm);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    loadPageData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPageData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const requests = isOrgOwner
        ? [
            axios.get(`${API}/team-members`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
            axios.get(`${API}/clients`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
          ]
        : [
            axios.get(`${API}/clients/accessible`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
          ];

      const responses = await Promise.all(requests);

      if (isOrgOwner) {
        setTeamMembers(responses[0].data || []);
        setClients(responses[1].data || []);
      } else {
        setClients(responses[0].data?.clients || []);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load workspace");
    } finally {
      setLoading(false);
    }
  };

  const employeeStats = useMemo(
    () => ({
      total: teamMembers.length,
      active: teamMembers.filter((member) => member.is_active).length,
    }),
    [teamMembers],
  );

  const clientStats = useMemo(
    () => ({
      total: clients.length,
      service: clients.filter((client) => client.company_type === "service_based")
        .length,
      product: clients.filter((client) => client.company_type === "product_based")
        .length,
    }),
    [clients],
  );

  const resetEmployeeDialog = () => {
    setEditingEmployee(null);
    setEmployeeForm(emptyEmployeeForm);
    setEmployeeDialogOpen(false);
  };

  const resetClientDialog = () => {
    setEditingClient(null);
    setClientForm(emptyClientForm);
    setClientDialogOpen(false);
  };

  const openEmployeeDialog = (employee = null) => {
    setEditingEmployee(employee);
    setEmployeeForm(
      employee
        ? {
            name: employee.name || "",
            email: employee.email || "",
            phone_number: employee.phone_number || "",
            role: employee.role || "",
            access_level: employee.access_level || "member",
            password: "",
          }
        : emptyEmployeeForm,
    );
    setEmployeeDialogOpen(true);
  };

  const openClientDialog = (client = null) => {
    setEditingClient(client);
    setClientForm(
      client
        ? {
            name: client.name || "",
            email: client.email || "",
            password: "",
            company_gst_number: client.company_gst_number || "",
            company_type: client.company_type || "product_based",
            assigned_team_member_ids: client.assigned_team_member_ids || [],
          }
        : emptyClientForm,
    );
    setClientDialogOpen(true);
  };

  const handleEmployeeSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const token = localStorage.getItem("token");
      const payload = {
        ...employeeForm,
        role: employeeForm.role || "CA Employee",
      };

      if (!payload.password && !editingEmployee) {
        toast.error("Password is required for a new CA employee");
        setIsSubmitting(false);
        return;
      }

      if (!payload.password) {
        delete payload.password;
      }

      if (editingEmployee) {
        await axios.put(`${API}/team-members/${editingEmployee.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("CA employee updated successfully");
      } else {
        await axios.post(`${API}/team-members`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("CA employee created successfully");
      }

      resetEmployeeDialog();
      loadPageData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save CA employee");
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleClientAssignment = (teamMemberId) => {
    setClientForm((prev) => {
      const exists = prev.assigned_team_member_ids.includes(teamMemberId);
      return {
        ...prev,
        assigned_team_member_ids: exists
          ? prev.assigned_team_member_ids.filter((id) => id !== teamMemberId)
          : [...prev.assigned_team_member_ids, teamMemberId],
      };
    });
  };

  const handleClientSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const token = localStorage.getItem("token");
      const payload = { ...clientForm };

      if (!payload.password && !editingClient) {
        toast.error("Password is required for a new client");
        setIsSubmitting(false);
        return;
      }

      if (!payload.password) {
        delete payload.password;
      }

      if (editingClient) {
        await axios.put(`${API}/clients/${editingClient.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Client updated successfully");
      } else {
        await axios.post(`${API}/clients`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Client created successfully");
      }

      resetClientDialog();
      loadPageData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save client");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEmployeeStatusToggle = async (employee) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API}/team-members/${employee.id}`,
        { is_active: !employee.is_active },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(
        employee.is_active
          ? "CA employee deactivated"
          : "CA employee activated",
      );
      loadPageData();
    } catch (error) {
      toast.error("Failed to update CA employee status");
    }
  };

  const handleClientStatusToggle = async (client) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API}/clients/${client.id}`,
        { is_active: !client.is_active },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(
        client.is_active ? "Client deactivated" : "Client activated",
      );
      loadPageData();
    } catch (error) {
      toast.error("Failed to update client status");
    }
  };

  const openClientWorkspace = (client) => {
    setActiveClient(client);
    navigate("/");
    window.location.reload();
  };

  return (
    <Layout>
      <div className="space-y-4" data-testid="team-members-page">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
          <div>
            <h1 className="text-lg font-bold bg-gradient-to-r from-primary-600 to-orange-600 bg-clip-text text-transparent">
              {isOrgOwner ? "Client & CA Employee Workspace" : "My Clients"}
            </h1>
            <p className="text-xs text-gray-500 mt-0.5">
              {isOrgOwner
                ? "Create CA employees, onboard clients, and assign the right handler from one place."
                : "Open the client workspace assigned to your CA employee account."}
            </p>
          </div>
          {isOrgOwner && (
            <div className="flex gap-2">
              <Button
                onClick={() => openEmployeeDialog()}
                className="bg-white border border-gray-300 text-gray-800 hover:bg-gray-50 text-xs"
              >
                <UserCog size={14} className="mr-1.5" />
                Add CA Employee
              </Button>
              <Button
                onClick={() => openClientDialog()}
                className="bg-gradient-to-r from-primary-500 to-orange-500 hover:from-primary-600 hover:to-orange-600 text-white text-xs"
              >
                <Plus size={14} className="mr-1.5" />
                Add Client
              </Button>
            </div>
          )}
        </div>

        {loading ? (
          <Card className="border-0 bg-white shadow-sm">
            <CardContent className="py-16 flex justify-center">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent"></div>
            </CardContent>
          </Card>
        ) : isOrgOwner ? (
          <>
            <div className="grid md:grid-cols-4 gap-3">
              <Card className="border-0 bg-gradient-to-br from-white to-gray-50 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-primary-100">
                      <Users size={18} className="text-primary-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">CA Employees</p>
                      <p className="text-lg font-bold text-gray-900">
                        {employeeStats.total}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-0 bg-gradient-to-br from-white to-gray-50 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-green-100">
                      <ShieldCheck size={18} className="text-green-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Active Employees</p>
                      <p className="text-lg font-bold text-gray-900">
                        {employeeStats.active}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-0 bg-gradient-to-br from-white to-gray-50 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-orange-100">
                      <FolderKanban size={18} className="text-orange-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">My Clients</p>
                      <p className="text-lg font-bold text-gray-900">
                        {clientStats.total}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-0 bg-gradient-to-br from-white to-gray-50 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-indigo-100">
                      <BriefcaseBusiness size={18} className="text-indigo-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Service Clients</p>
                      <p className="text-lg font-bold text-gray-900">
                        {clientStats.service}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Tabs defaultValue="employees" className="space-y-3">
              <TabsList className="grid w-full grid-cols-2 max-w-md bg-gray-100">
                <TabsTrigger value="employees" className="text-xs">
                  CA Employee
                </TabsTrigger>
                <TabsTrigger value="clients" className="text-xs">
                  My Clients
                </TabsTrigger>
              </TabsList>

              <TabsContent value="employees">
                <Card className="border-0 bg-gradient-to-br from-white to-gray-50 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-sm font-bold text-gray-800">
                      CA Employee Directory
                    </CardTitle>
                    <CardDescription className="text-xs text-gray-500">
                      Every CA employee gets a linked login account so they can directly access the portal.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-xs">Employee</TableHead>
                            <TableHead className="text-xs">Role</TableHead>
                            <TableHead className="text-xs">Access</TableHead>
                            <TableHead className="text-xs">Clients</TableHead>
                            <TableHead className="text-xs">Status</TableHead>
                            <TableHead className="text-xs text-right">
                              Actions
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {teamMembers.map((member) => {
                            const clientCount = clients.filter((client) =>
                              (client.assigned_team_member_ids || []).includes(member.id),
                            ).length;
                            return (
                              <TableRow key={member.id}>
                                <TableCell>
                                  <div className="space-y-1">
                                    <div className="font-medium text-sm text-gray-900">
                                      {member.name}
                                    </div>
                                    <div className="text-xs text-gray-500 flex items-center gap-3">
                                      <span className="inline-flex items-center gap-1">
                                        <Mail size={11} />
                                        {member.email}
                                      </span>
                                      {member.phone_number && (
                                        <span className="inline-flex items-center gap-1">
                                          <Phone size={11} />
                                          {member.phone_number}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell className="text-sm">
                                  {member.role || "CA Employee"}
                                </TableCell>
                                <TableCell>
                                  <Badge variant="outline" className="text-xs capitalize">
                                    {member.access_level}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-sm font-medium">
                                  {clientCount}
                                </TableCell>
                                <TableCell>
                                  <Badge
                                    className={
                                      member.is_active
                                        ? "bg-green-100 text-green-700 hover:bg-green-100"
                                        : "bg-red-100 text-red-700 hover:bg-red-100"
                                    }
                                  >
                                    {member.is_active ? "Active" : "Inactive"}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-right">
                                  <div className="flex justify-end gap-2">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="h-8 text-xs"
                                      onClick={() => openEmployeeDialog(member)}
                                    >
                                      <Edit size={12} className="mr-1" />
                                      Edit
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="h-8 text-xs"
                                      onClick={() => handleEmployeeStatusToggle(member)}
                                    >
                                      {member.is_active ? "Deactivate" : "Activate"}
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="clients">
                <Card className="border-0 bg-gradient-to-br from-white to-gray-50 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-sm font-bold text-gray-800">
                      My Clients
                    </CardTitle>
                    <CardDescription className="text-xs text-gray-500">
                      Each client gets a portal login, GST profile, company type, and assigned handler mapping.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid lg:grid-cols-2 gap-3">
                      {clients.map((client) => (
                        <Card key={client.id} className="border border-gray-200 shadow-none">
                          <CardContent className="p-4 space-y-3">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <h3 className="font-semibold text-sm text-gray-900">
                                  {client.name}
                                </h3>
                                <p className="text-xs text-gray-500 mt-1">{client.email}</p>
                              </div>
                              <Badge
                                className={
                                  client.is_active
                                    ? "bg-green-100 text-green-700 hover:bg-green-100"
                                    : "bg-red-100 text-red-700 hover:bg-red-100"
                                }
                              >
                                {client.is_active ? "Active" : "Inactive"}
                              </Badge>
                            </div>

                            <div className="grid grid-cols-2 gap-3 text-xs text-gray-600">
                              <div>
                                <div className="text-gray-400 uppercase tracking-wide">
                                  Company GST
                                </div>
                                <div className="font-medium text-gray-800 mt-1">
                                  {client.company_gst_number || "-"}
                                </div>
                              </div>
                              <div>
                                <div className="text-gray-400 uppercase tracking-wide">
                                  Company Type
                                </div>
                                <div className="font-medium text-gray-800 mt-1">
                                  {client.company_type === "service_based"
                                    ? "Service Based"
                                    : "Product Based"}
                                </div>
                              </div>
                            </div>

                            <div>
                              <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-2">
                                Assigned Handlers
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {(client.assigned_handlers || []).length > 0 ? (
                                  client.assigned_handlers.map((handler) => (
                                    <Badge key={handler.id} variant="outline" className="text-xs">
                                      {handler.name}
                                    </Badge>
                                  ))
                                ) : (
                                  <span className="text-xs text-gray-500">
                                    No CA employee assigned yet
                                  </span>
                                )}
                              </div>
                            </div>

                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 text-xs"
                                onClick={() => openClientDialog(client)}
                              >
                                <Edit size={12} className="mr-1" />
                                Edit
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 text-xs"
                                onClick={() => handleClientStatusToggle(client)}
                              >
                                {client.is_active ? "Deactivate" : "Activate"}
                              </Button>
                              <Button
                                size="sm"
                                className="h-8 text-xs bg-gradient-to-r from-primary-500 to-orange-500 text-white ml-auto"
                                onClick={() => openClientWorkspace(client)}
                              >
                                Open Workspace
                                <ArrowRight size={12} className="ml-1.5" />
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </>
        ) : (
          <div className="grid lg:grid-cols-2 gap-3">
            {clients.length > 0 ? (
              clients.map((client) => (
                <Card key={client.id} className="border-0 bg-gradient-to-br from-white to-gray-50 shadow-sm">
                  <CardContent className="p-5 space-y-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="font-semibold text-base text-gray-900">
                          {client.name}
                        </h3>
                        <p className="text-xs text-gray-500 mt-1">{client.email}</p>
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {client.company_type === "service_based"
                          ? "Service Based"
                          : "Product Based"}
                      </Badge>
                    </div>

                    <div className="grid grid-cols-2 gap-3 text-xs text-gray-600">
                      <div className="rounded-lg bg-gray-50 p-3">
                        <div className="text-gray-400 uppercase tracking-wide">
                          GST
                        </div>
                        <div className="font-medium text-gray-800 mt-1">
                          {client.company_gst_number || "-"}
                        </div>
                      </div>
                      <div className="rounded-lg bg-gray-50 p-3">
                        <div className="text-gray-400 uppercase tracking-wide">
                          Handlers
                        </div>
                        <div className="font-medium text-gray-800 mt-1">
                          {(client.assigned_handlers || []).length}
                        </div>
                      </div>
                    </div>

                    <div>
                      <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-2">
                        Assigned Team
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {(client.assigned_handlers || []).map((handler) => (
                          <Badge key={handler.id} variant="outline" className="text-xs">
                            {handler.name}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    <Button
                      onClick={() => openClientWorkspace(client)}
                      className="w-full bg-gradient-to-r from-primary-500 to-orange-500 hover:from-primary-600 hover:to-orange-600 text-white text-xs"
                    >
                      Open Client Workspace
                      <ArrowRight size={12} className="ml-1.5" />
                    </Button>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card className="border-0 bg-white shadow-sm lg:col-span-2">
                <CardContent className="py-12 text-center">
                  <UserCircle2 className="mx-auto h-10 w-10 text-gray-300" />
                  <h3 className="mt-4 text-sm font-semibold text-gray-900">
                    No client assigned yet
                  </h3>
                  <p className="mt-2 text-xs text-gray-500">
                    Ask your organization admin to assign one or more client workspaces to your CA employee account.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>

      <Dialog open={employeeDialogOpen} onOpenChange={setEmployeeDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-base font-bold flex items-center gap-2">
              <Users size={16} />
              {editingEmployee ? "Edit CA Employee" : "Create CA Employee"}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {editingEmployee
                ? "Update the CA employee profile and linked portal access."
                : "Create a CA employee and linked portal login in one step."}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleEmployeeSubmit} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs">Name</Label>
                <Input
                  value={employeeForm.name}
                  onChange={(e) =>
                    setEmployeeForm((prev) => ({ ...prev, name: e.target.value }))
                  }
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Phone Number</Label>
                <Input
                  value={employeeForm.phone_number}
                  onChange={(e) =>
                    setEmployeeForm((prev) => ({
                      ...prev,
                      phone_number: e.target.value,
                    }))
                  }
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs">Email</Label>
                <Input
                  type="email"
                  value={employeeForm.email}
                  onChange={(e) =>
                    setEmployeeForm((prev) => ({ ...prev, email: e.target.value }))
                  }
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Password</Label>
                <Input
                  type="password"
                  value={employeeForm.password}
                  onChange={(e) =>
                    setEmployeeForm((prev) => ({
                      ...prev,
                      password: e.target.value,
                    }))
                  }
                  placeholder={editingEmployee ? "Leave blank to keep current" : ""}
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs">Role</Label>
                <Input
                  value={employeeForm.role}
                  onChange={(e) =>
                    setEmployeeForm((prev) => ({ ...prev, role: e.target.value }))
                  }
                  placeholder="Senior CA, Auditor, GST Specialist"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Access Level</Label>
                <Select
                  value={employeeForm.access_level}
                  onValueChange={(value) =>
                    setEmployeeForm((prev) => ({ ...prev, access_level: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="member">Member</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={resetEmployeeDialog}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="bg-gradient-to-r from-primary-500 to-orange-500 text-white"
              >
                {isSubmitting
                  ? "Saving..."
                  : editingEmployee
                    ? "Save Changes"
                    : "Create CA Employee"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={clientDialogOpen} onOpenChange={setClientDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-base font-bold flex items-center gap-2">
              <Building2 size={16} />
              {editingClient ? "Edit Client" : "Create Client"}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {editingClient
                ? "Update client login details, GST profile, and handler assignment."
                : "Create a client portal account and assign the right CA employee handler."}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleClientSubmit} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs">Client Name</Label>
                <Input
                  value={clientForm.name}
                  onChange={(e) =>
                    setClientForm((prev) => ({ ...prev, name: e.target.value }))
                  }
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Email</Label>
                <Input
                  type="email"
                  value={clientForm.email}
                  onChange={(e) =>
                    setClientForm((prev) => ({ ...prev, email: e.target.value }))
                  }
                  required
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs">Password</Label>
                <Input
                  type="password"
                  value={clientForm.password}
                  onChange={(e) =>
                    setClientForm((prev) => ({
                      ...prev,
                      password: e.target.value,
                    }))
                  }
                  placeholder={editingClient ? "Leave blank to keep current" : ""}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Company GST</Label>
                <Input
                  value={clientForm.company_gst_number}
                  onChange={(e) =>
                    setClientForm((prev) => ({
                      ...prev,
                      company_gst_number: e.target.value.toUpperCase(),
                    }))
                  }
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs">Company Type</Label>
              <Select
                value={clientForm.company_type}
                onValueChange={(value) =>
                  setClientForm((prev) => ({ ...prev, company_type: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="product_based">Product Based</SelectItem>
                  <SelectItem value="service_based">Service Based</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-xs">Assign Handler</Label>
              <div className="grid sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto rounded-lg border border-gray-200 p-3">
                {teamMembers.length > 0 ? (
                  teamMembers
                    .filter((member) => member.is_active)
                    .map((member) => {
                      const selected = clientForm.assigned_team_member_ids.includes(
                        member.id,
                      );
                      return (
                        <button
                          key={member.id}
                          type="button"
                          onClick={() => toggleClientAssignment(member.id)}
                          className={`text-left rounded-lg border p-3 transition-all ${
                            selected
                              ? "border-primary-500 bg-primary-50"
                              : "border-gray-200 hover:border-primary-300"
                          }`}
                        >
                          <div className="font-medium text-sm text-gray-900">
                            {member.name}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {member.role || "CA Employee"}
                          </div>
                        </button>
                      );
                    })
                ) : (
                  <div className="text-xs text-gray-500">
                    Create a CA employee first to assign handlers.
                  </div>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={resetClientDialog}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="bg-gradient-to-r from-primary-500 to-orange-500 text-white"
              >
                {isSubmitting
                  ? "Saving..."
                  : editingClient
                    ? "Save Changes"
                    : "Create Client"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
